import React, { useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Table,
  Tag,
  message,
  Space,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../../nestserver";

const { Option } = require("antd/lib/select");

const P = "#09617e";
const P_LIGHT = "#e8f4f7";
const P_BORDER = "#afd5e3";

const SectionHeader = ({ label }) => (
  <div
    style={{
      marginBottom: 14,
      fontSize: 11,
      fontWeight: 700,
      color: P,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {label}
  </div>
);

const FormCard = ({ children, style }) => (
  <div
    style={{
      background: "#f7f9fa",
      border: "1px solid #eaeff2",
      borderRadius: 10,
      padding: "16px 16px 2px",
      marginBottom: 14,
      ...style,
    }}
  >
    {children}
  </div>
);

const TransportBillingZoneManager = ({ visible, onClose }) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [editingZone, setEditingZone] = useState(null);
  const [zoneModal, setZoneModal] = useState(false);
  const [destinations, setDestinations] = useState([{ name: "" }]);
  const [saving, setSaving] = useState(false);

  const headers = {
    Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
  };

  const fetchZones = async () => {
    const res = await axios.get(
      `${serverUrl}/admin/transport-billing-zone/list`,
      { headers }
    );
    return res.data?.result || [];
  };

  const zonesQuery = useQuery(["transport-billing-zones"], fetchZones, {
    enabled: visible,
  });

  const openAddModal = () => {
    setEditingZone(null);
    setDestinations([{ name: "" }]);
    form.resetFields();
    setZoneModal(true);
  };

  const openEditModal = (zone) => {
    setEditingZone(zone);
    const existingDests =
      zone.destinations?.length > 0
        ? zone.destinations.map((d) => ({ name: d.name }))
        : [{ name: "" }];
    setDestinations(existingDests);
    const zoneAmount = zone.destinations?.[0]?.amount ?? zone.fee_amount ?? "";
    form.setFieldsValue({ name: zone.name, amount: zoneAmount ? String(zoneAmount) : "" });
    setZoneModal(true);
  };

  const closeModal = () => {
    setZoneModal(false);
    form.resetFields();
    setDestinations([{ name: "" }]);
  };

  const addDestination = () =>
    setDestinations((prev) => [...prev, { name: "" }]);

  const removeDestination = (index) =>
    setDestinations((prev) => prev.filter((_, i) => i !== index));

  const updateDestination = (index, value) =>
    setDestinations((prev) =>
      prev.map((d, i) => (i === index ? { name: value } : d))
    );

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const validDests = destinations.filter((d) => d.name.trim());
      if (validDests.length === 0) {
        message.error("Add at least one destination");
        return;
      }

      const amount = parseFloat(values.amount) || 0;

      setSaving(true);
      const payload = {
        name: values.name,
        destinations: validDests.map((d) => ({
          name: d.name.trim(),
          amount,
        })),
      };

      if (editingZone) {
        await axios.patch(
          `${serverUrl}/admin/transport-billing-zone/${editingZone._id}`,
          payload,
          { headers }
        );
        message.success("Billing zone updated");
      } else {
        await axios.post(
          `${serverUrl}/admin/transport-billing-zone`,
          payload,
          { headers }
        );
        message.success("Billing zone created");
      }
      queryClient.invalidateQueries(["transport-billing-zones"]);
      closeModal();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to save billing zone";
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (zone) => {
    Modal.confirm({
      title: "Delete this billing zone?",
      content: `"${zone.name}" will be removed. Existing fee templates using this zone are unaffected.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(
            `${serverUrl}/admin/transport-billing-zone/${zone._id}`,
            { headers }
          );
          message.success("Billing zone deleted");
          queryClient.invalidateQueries(["transport-billing-zones"]);
        } catch {
          message.error("Failed to delete billing zone");
        }
      },
    });
  };

  const columns = [
    {
      title: "Zone Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: P,
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          <span style={{ fontWeight: 600, color: "#1a2e35" }}>{name}</span>
        </div>
      ),
    },
    {
      title: "Destinations",
      dataIndex: "destinations",
      key: "destinations",
      render: (dests) => {
        if (!dests?.length) return (
          <span style={{ color: "#b0bec5", fontSize: 12, fontStyle: "italic" }}>No destinations</span>
        );
        const amount = dests[0]?.amount;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
            {dests.map((d, i) => (
              <Tag
                key={d._id || i}
                style={{
                  background: P_LIGHT,
                  color: P,
                  border: `1px solid ${P_BORDER}`,
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 500,
                  lineHeight: "20px",
                  padding: "0 8px",
                  margin: 0,
                }}
              >
                {d.name}
              </Tag>
            ))}
            {amount != null && (
              <Tag
                style={{
                  background: "#f0fdf4",
                  color: "#166534",
                  border: "1px solid #bbf7d0",
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: "20px",
                  padding: "0 8px",
                  margin: 0,
                }}
              >
                ₹{Number(amount).toLocaleString("en-IN")}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 96,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <Button
            size="small"
            onClick={() => openEditModal(record)}
            style={{ borderColor: P, color: P, borderRadius: 6 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            danger
            onClick={() => handleDelete(record)}
            style={{ borderRadius: 6 }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* ── Main: Van Fee Zones list ─────────────────────────────────── */}
      <Modal
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#1a2e35" }}>
              Van Fee Zones
            </div>
            <div
              style={{ fontWeight: 400, fontSize: 12, color: "#8a9faa", marginTop: 2 }}
            >
              Manage transport zones and their pickup destinations
            </div>
          </div>
        }
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={960}
        destroyOnClose
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <p style={{ color: "#637a84", fontSize: 13, margin: 0 }}>
            Each zone groups pickup destinations with a single annual fee amount.
          </p>
          <Button
            onClick={openAddModal}
            style={{
              background: P,
              borderColor: P,
              color: "white",
              fontWeight: 600,
              borderRadius: 8,
              height: 36,
              paddingInline: 18,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              boxShadow: `0 2px 6px ${P}40`,
            }}
          >
            Add Zone
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={zonesQuery.data || []}
          rowKey="_id"
          loading={zonesQuery.isLoading}
          pagination={false}
          locale={{
            emptyText: (
              <div style={{ padding: "44px 0", textAlign: "center" }}>
                <div
                  style={{ fontWeight: 600, fontSize: 14, color: "#3a5560" }}
                >
                  No billing zones defined
                </div>
                <div
                  style={{ fontSize: 12, color: "#9db0b8", marginTop: 4 }}
                >
                  Click "Add Zone" to create your first van fee zone
                </div>
              </div>
            ),
          }}
        />
      </Modal>

      {/* ── Add / Edit Zone ───────────────────────────────────────────── */}
      <Modal
        title={
          <div>
            <div
              style={{ fontWeight: 700, fontSize: 15, color: "#1a2e35" }}
            >
              {editingZone ? "Edit Billing Zone" : "Add Billing Zone"}
            </div>
            <div
              style={{
                fontWeight: 400,
                fontSize: 12,
                color: "#8a9faa",
                marginTop: 2,
              }}
            >
              {editingZone
                ? `Editing: ${editingZone.name}`
                : "Create a new zone with pickup destinations"}
            </div>
          </div>
        }
        visible={zoneModal}
        onCancel={closeModal}
        onOk={handleSave}
        okText={editingZone ? "Update Zone" : "Create Zone"}
        okButtonProps={{
          style: {
            background: editingZone ? "#d97706" : P,
            borderColor: editingZone ? "#d97706" : P,
            fontWeight: 600,
            borderRadius: 7,
            height: 34,
          },
        }}
        cancelButtonProps={{ style: { borderRadius: 7, height: 34 } }}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          {/* Zone name + amount */}
          <FormCard>
            <SectionHeader label="Zone Details" />
            <Form.Item
              label="Zone Name"
              name="name"
              rules={[{ required: true, message: "Zone name is required" }]}
            >
              <Input
                placeholder="e.g. Van Route A – North"
                style={{ borderRadius: 7 }}
              />
            </Form.Item>
            <Form.Item
              label="Annual Fee Amount (₹)"
              name="amount"
              rules={[{ required: true, message: "Fee amount is required" }]}
            >
              <Input
                type="number"
                min={0}
                placeholder="Amount applied to all destinations in this zone"
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown")
                    e.preventDefault();
                }}
                style={{ borderRadius: 7 }}
                prefix="₹"
              />
            </Form.Item>
          </FormCard>

          {/* Destinations */}
          <FormCard>
            <SectionHeader label="Destinations" />
            <div style={{ fontSize: 12, color: "#637a84", marginBottom: 12 }}>
              Add each pickup location. All destinations share the fee amount above.
            </div>

            {destinations.map((dest, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  marginBottom: 10,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Input
                    placeholder="Destination name (e.g. Gundlakattamachi)"
                    value={dest.name}
                    onChange={(e) => updateDestination(index, e.target.value)}
                    style={{ borderRadius: 7 }}
                  />
                </div>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={destinations.length === 1}
                  onClick={() => removeDestination(index)}
                  style={{ marginTop: 1 }}
                />
              </div>
            ))}

            <Button
              type="dashed"
              onClick={addDestination}
              style={{
                width: "100%",
                borderRadius: 7,
                marginTop: 4,
                marginBottom: 8,
                color: P,
                borderColor: P_BORDER,
              }}
            >
              Add Destination
            </Button>
          </FormCard>
        </Form>
      </Modal>
    </>
  );
};

export default TransportBillingZoneManager;
